<template>
  <div
    :class="[tileType]"
    class="carousel-component"
  >
    <carousel-header
      v-if="showHeader"
      :item-count="tileCount"
      :item-label-singular="itemLabelSingular"
      :item-label-plural="itemLabelPlural"
      :show-navigation="true"
      :can-navigate-right="showNavigationButton('right')"
      :can-navigate-left="showNavigationButton('left')"
      :view-all-route="viewAllRoute"
      @navigate="navigate"
      @view-all-clicked="$emit('view-all-clicked')"
    >
      <template v-slot:heading>
        <slot name="heading" />
      </template>
    </carousel-header>
    <div
      :class="[tileType]"
      class="carousel-body"
    >
      <button
        v-if="!showHeader && showNavigationButton('left')"
        class="left"
        aria-label="Show previous"
        @click="navigate('left')"
      >
        <icon
          class="arrow-icon"
          name="mastery-arrow-left"
          variant="b2b2b2"
        />
      </button>
      <button
        v-if="!showHeader && showNavigationButton('right')"
        class="right"
        aria-label="Show next"
        @click="navigate('right')"
      >
        <icon
          class="arrow-icon"
          name="mastery-arrow-right"
          variant="b2b2b2"
        />
      </button>
      <div
        ref="nav"
        class="tile-container"
        @scroll="checkScrollPosition"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
import CarouselHeader from '@/components/carousels/carousel-header.vue';
import Icon from '@components/singletons/icon';

export default {
  components: { CarouselHeader, Icon },
  props: {
    itemWidth: {
      type: Number,
      default: 128, // this is the total width of each category box including margin
    },
    itemLabelSingular: {
      type: String,
      default: 'Item',
    },
    itemLabelPlural: {
      type: String,
      default: 'Items',
    },
    showHeader: {
      type: Boolean,
      default: false,
    },
    tileCount: {
      type: Number,
      default: 0,
    },
    tileType: {
      type: String,
      default: 'standard',
    },
    viewAllRoute: {
      type: Object,
    },
  },
  data() {
    return {
      scrollPosition: 'start',
    };
  },
  mounted() {
    if (this.$refs.nav) {
      this.checkScrollPosition();
    }

    window.addEventListener('resize', this.checkScrollPosition);

    this.$watch('tileCount', (newVal, oldVal) => {
      if (newVal !== oldVal) {
        if (this.$refs.nav) {
          this.checkScrollPosition();
        }
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScrollPosition);
  },
  methods: {
    checkScrollPosition() {
      const position = this.$refs.nav.scrollLeft;
      const width = this.$refs.nav.getBoundingClientRect().width;

      if (Math.floor(width / this.itemWidth) >= this.tileCount) {
        // if there's plenty of space and no need to scroll, don't show any arrows by setting to contained
        this.scrollPosition = 'contained';
      } else if (position === 0) {
        this.scrollPosition = 'start';
      } else if ((position + width) >= this.tileCount * this.itemWidth) {
        this.scrollPosition = 'end';
      } else {
        this.scrollPosition = 'middle';
      }
      this.$emit('scroll-event', { position, width });
    },
    navigate(direction) {
      const scroll = this.$refs.nav.scrollLeft;
      const width = this.$refs.nav.getBoundingClientRect().width;
      const distance = scroll + width;
      // if user is going left, the item left of the first fully shown item should become the last fully shown item
      // With larger block tiles, there may only be a single block tile that is fully shown in narrow parent elements
      // When this occurs, we need to scroll a full tile back
      // if user is going right, the first item to appear after clicking right is the first not fully shown item on right
      if (direction === 'left') {
        const offset = distance % this.itemWidth === 0 ? 0 : 1;
        const maxTiles = Math.floor(width / this.itemWidth);
        if (maxTiles === 1) {
          this.$refs.nav.scroll({ left: scroll - this.itemWidth, behavior: 'smooth' });
        }
        const number = Math.floor(scroll / this.itemWidth) - maxTiles + offset;
        this.$refs.nav.scroll({ left: number * this.itemWidth, behavior: 'smooth' });
      } else if (direction === 'right') {
        const number = Math.floor(distance / this.itemWidth);
        this.$refs.nav.scroll({ left: number * this.itemWidth, behavior: 'smooth' });
      }
      this.$emit('handle-carousel-navigated');
    },
    showNavigationButton(direction) {
      if (this.scrollPosition !== 'contained') {
        if (direction === 'left') {
          return this.scrollPosition !== 'start';
        } else if (direction === 'right') {
          return this.scrollPosition !== 'end';
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.carousel-component {
  .carousel-body {
    .tile-container {
      // height: rem-calc(120);
      overflow-x: auto;
      overflow-y: hidden;
      padding: rem-calc(10) 0;
      -webkit-overflow-scrolling: touch;
      -ms-overflow-style: none;
      scrollbar-width: none;
      white-space: nowrap;

      &::-webkit-scrollbar {
        display: none;
      }

      @include breakpoint(small) {
        margin: 0 rem-calc(-24);

        > * {
          // extra margin since we are negating padding in container
          &:first-child {
            @include breakpoint(small) {
              margin-left: rem-calc(24) !important;
            }
          }

          &:last-child {
            @include breakpoint(small) {
              margin-right: rem-calc(24) !important;
            }
          }
        }
      }
    }

    > button {
      background: #f4f4f4;
      border-radius: 100%;
      padding: rem-calc(4);

      @include breakpoint(small) {
        display: none;
      }

      .arrow-icon {
        height: rem-calc(14);
        width: rem-calc(14);
        display: block;
      }

      &.left {
        float: left;
        margin-left: rem-calc(-62);
      }

      &.right {
        float: right;
        margin-right: rem-calc(-62);
      }

      @media only screen and (max-width: 1150px) {
        &.left {
          margin-left: rem-calc(-46);
        }
        &.right {
          margin-right: rem-calc(-46);
        }
      }
    }

    &.standard {
      .tile-container {
        > * {
          // margin should match box shadow otherwise shadow is hidden
          &:first-child {
            margin-left: rem-calc(4);
          }

          &:last-child {
            margin-right: rem-calc(4);
          }
        }
      }

      > button {
        // should just be centered with link box
        // 100 / 2 (half height of tile) + 22 / 2 (half height of button) + 2 (border of first tile)
        // placed in dom above tiles so it needs to be moved down from initial position
        margin-top: rem-calc(59);
      }
    }

    &.block {
      @include breakpoint(small) {
        margin: 0;
      }

      .tile-container {
        // height from block tile
        padding: rem-calc(8) 0;
      }

      > button {
        // should just be centered with image
        // 193.7 / 2 (half height of image) - (22 / 2 (half height of button)) + 12 (padding of tile container)
        // placed in dom above tiles so it needs to be moved down from initial position
        margin-top: rem-calc(100);
        // now button margin must account for extra negative margin on carousel container
        margin-left: rem-calc(-51.5);

        @media only screen and (max-width: 1150px) {
          margin-left: rem-calc(-35.5);
        }
      }
    }

    &.catalog {
      > button {
        // should just be centered with link box
        // 296 / 2 (half height of tile) + 22 / 2 (half height of button) + 2 (border of first tile)
        // placed in dom above tiles so it needs to be moved down from initial position
        margin-top: rem-calc(161);
      }
    }

    &.catalog-review {
      > button {
        &.left {
          margin-top: rem-calc(96);
        }

        &.right {
          margin-top: rem-calc(96);
        }

        @include breakpoint(medium) {
          &.left {
            margin-left: rem-calc(-24);
          }

          &.right {
            margin-right: rem-calc(-24);
          }
        }
      }
    }

    &.catalog-marquee {
      > button {
        &.left {
          margin-top: rem-calc(176);
        }

        &.right {
          margin-top: rem-calc(176);
        }

        @include breakpoint(medium) {
          &.left {
            margin-left: rem-calc(-24);
          }

          &.right {
            margin-right: rem-calc(-24);
          }
        }
      }
    }
  }
}
</style>
